// Date/duration helpers. dayjs handles parsing and UTC→local conversion.
// Add `dayjs/plugin/timezone` here if explicit IANA tz output is ever needed.

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

// No embedded timezone → treat as UTC.
const UTC_FORMATS = [
  "DD-MM-YYYY hh:mm:ss A",
  "DD-MM-YYYY HH:mm:ss",
  "DD-MM-YYYY",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss.SSS",
];

const parse = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  // ISO with Z or numeric offset — dayjs handles the tz natively.
  if (/[Zz]$|[+\-]\d{2}:?\d{2}$/.test(s)) {
    const d = dayjs(s);
    return d.isValid() ? d : null;
  }
  // customParseFormat parses in LOCAL time even via dayjs.utc(). `.utc(true)`
  // keeps wall-clock values and re-tags them as UTC — without it "10:30 AM"
  // gets read as local and silently shifted.
  const d = dayjs(s, UTC_FORMATS, true).utc(true);
  return d.isValid() ? d : null;
};

export const formatLocalDate = (value) => {
  const d = parse(value);
  if (!d) return value ? String(value) : "";
  return d.local().format("MMM DD, YYYY hh:mm A");
};

export const formatShortDate = (value) => {
  const d = parse(value);
  if (!d) return value ? String(value) : "";
  return d.local().format("MMM DD, YYYY");
};

const pad = (n) => (n < 10 ? "0" + n : String(n));

// Seconds → "MM:SS" or "HH:MM:SS"; falsy/0 → "Not started".
export const formatDuration = (secs) => {
  const n = Number(secs);
  if (!Number.isFinite(n) || n <= 0) return "Not started";
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = Math.floor(n % 60);
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
};

// Bytes → "1.2 MB" / "300 KB" / "0 B" / "-" for nullish. String input is
// coerced. Whole-byte values omit the decimal (Hubspot reference behavior).
export const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined || bytes === "") return "-";
  const num = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;
  if (!Number.isFinite(num)) return String(bytes);
  if (num === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(num) / Math.log(1024)));
  const size = (num / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0);
  return `${size} ${units[i]}`;
};

// 1=SMS, 2=WhatsApp, 3=Email, 4=Smartglass, 7=URL
export const formatInviteType = (t) => {
  switch (Number(t)) {
    case 1: return "SMS";
    case 2: return "WhatsApp";
    case 3: return "Email";
    case 4: return "Smartglass";
    case 7: return "URL";
    default: return "—";
  }
};
